import React, { useCallback, useMemo } from "react";
import type { ParamOverride, ParameterSchema } from "@tradingdroid/frontends-types";

import { ParamOverrideItem } from "./ParamOverrideItem";
import { ParamOverridesProvider } from "./ParamOverridesContext";
import type { ParamInfo } from "./types";
import {
  buildFullOverridesPayload,
  describeEmptyConditionalBranch,
  findEmptyConditionalBranches,
  getCurrentValues,
  isParamVisible,
} from "./utils";

// Parameters hidden from optimization UI (no effect in backtests)
const HIDDEN_PARAMS = new Set(["stop_loss_timing", "take_profit_timing"]);

interface Props {
  parameterSchema: Record<string, ParameterSchema>;
  strategyDefaults?: Record<string, unknown>;
  overrides: Record<string, ParamOverride>;
  onChange: (overrides: Record<string, ParamOverride>) => void;
  disabled?: boolean;
}

export const ParamOverridesEditor: React.FC<Props> = ({
  parameterSchema,
  strategyDefaults = {},
  overrides,
  onChange,
  disabled = false,
}) => {
  // Compute current values for dependency evaluation
  const currentValues = useMemo(
    () => getCurrentValues(parameterSchema, strategyDefaults, overrides),
    [parameterSchema, strategyDefaults, overrides],
  );

  // Merge schema params and strategy default params into a unified list
  // Order: 1) Default params first (in defaults order), 2) Non-default params alphabetically
  const paramList = useMemo((): ParamInfo[] => {
    const result: ParamInfo[] = [];
    const addedParams = new Set<string>();

    // 1. First: params that have default values (in defaults order)
    for (const name of Object.keys(strategyDefaults)) {
      const schema = parameterSchema[name] ?? null;
      result.push({
        name,
        schema,
        defaultValue: strategyDefaults[name],
        currentOverride: overrides[name],
        isSchemaOnly: false,
      });
      addedParams.add(name);
    }

    // 2. Then: schema params without default values (sorted alphabetically)
    const nonDefaultParams = Object.entries(parameterSchema)
      .filter(([name]) => !addedParams.has(name))
      .sort(([nameA], [nameB]) => nameA.localeCompare(nameB));

    for (const [name, schema] of nonDefaultParams) {
      result.push({
        name,
        schema,
        defaultValue: undefined,
        currentOverride: overrides[name],
        isSchemaOnly: true,
      });
    }

    return result;
  }, [parameterSchema, strategyDefaults, overrides]);

  // Filter to only visible params based on dependencies and hidden list
  const visibleParamList = useMemo(() => {
    return paramList.filter((param) => {
      // Hide params that are noise for optimization (e.g., timing params have no effect in backtests)
      if (HIDDEN_PARAMS.has(param.name)) return false;
      if (!param.schema) return true;
      return isParamVisible(param.schema.dependencies, currentValues, parameterSchema, overrides);
    });
  }, [paramList, currentValues, parameterSchema, overrides]);

  // Checked on the overrides payload built from these inputs, not on the raw overrides.
  const emptyBranches = useMemo(
    () =>
      findEmptyConditionalBranches(
        parameterSchema,
        buildFullOverridesPayload(parameterSchema, strategyDefaults, overrides),
      ),
    [parameterSchema, strategyDefaults, overrides],
  );

  const updateOverride = useCallback(
    (paramName: string, override: ParamOverride | undefined) => {
      const newOverrides = { ...overrides };
      if (override === undefined) {
        delete newOverrides[paramName];
      } else {
        newOverrides[paramName] = override;
      }
      onChange(newOverrides);
    },
    [overrides, onChange],
  );

  if (visibleParamList.length === 0) {
    return <div className="text-body-secondary small">No parameters available</div>;
  }

  return (
    <ParamOverridesProvider
      disabled={disabled}
      parameterSchema={parameterSchema}
      overrides={overrides}
      currentValues={currentValues}
      updateOverride={updateOverride}
    >
      <div className="ParamOverridesEditor">
        {emptyBranches.length > 0 && (
          <div className="EmptyConditionalBranches text-danger small mb-2" role="alert">
            <div className="EmptyConditionalBranchesTitle">
              These values leave nothing to search:
            </div>
            {emptyBranches.map((branch) => (
              <div key={`${branch.param}:${branch.parentValue}`} className="EmptyConditionalBranch">
                {describeEmptyConditionalBranch(branch)}
              </div>
            ))}
          </div>
        )}
        {visibleParamList.map((param) => (
          <ParamOverrideItem key={param.name} param={param} />
        ))}
      </div>
    </ParamOverridesProvider>
  );
};
